import { performanceStore } from "../performance/store";
import { WebRtcStatsCollector } from "../performance/webrtcStats";
import { encodeKeyboardInput, encodeMouseInput, encodeTouchInput, type DirectTouch } from "./protobuf";
import { directControl } from "./controlStore";

export type StreamProfile = "ultra-low-latency" | "balanced" | "quality";

interface Callbacks {
  onTrack: (track: MediaStreamTrack) => void;
  onState: (state: string) => void;
  onError: (error: Error) => void;
}

type Signal = {
  start?: RTCConfiguration;
  type?: RTCSdpType;
  sdp?: RTCSessionDescriptionInit | string;
  candidate?: RTCIceCandidateInit | string;
  bye?: boolean;
};

export class AndroidWebRtcClient {
  peerConnection?: RTCPeerConnection;
  inputChannel?: RTCDataChannel;
  private socket?: WebSocket;
  private stats?: WebRtcStatsCollector;
  private pendingCandidates: (RTCIceCandidateInit | string)[] = [];
  private remoteDescriptionSet = false;
  private stopped = false;
  private reconnectAttempt = 0;
  private reconnectTimer?: number;
  private signalChain = Promise.resolve();

  constructor(
    private readonly signalingUrl: string,
    private readonly callbacks: Callbacks,
    private profile: StreamProfile,
  ) {}

  connect() {
    this.stopped = false;
    this.callbacks.onState("connecting");
    this.openSocket();
  }

  disconnect() {
    this.stopped = true;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.cleanupConnection();
    this.callbacks.onState("disconnected");
  }

  reconnect() {
    this.stopped = false;
    this.reconnectAttempt = 0;
    if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.cleanupConnection();
    this.callbacks.onState("connecting");
    // Give the emulator time to release its previous RTC peer after `bye`.
    // Opening a replacement immediately can create offer glare in the native
    // emulator WebRTC implementation.
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, 500);
  }

  setProfile(profile: StreamProfile) {
    if (this.profile === profile) return;
    this.profile = profile;
    this.reconnect();
  }

  sendMouse(x: number, y: number, buttons: number, essential = false) {
    this.send(encodeMouseInput(x, y, buttons), essential);
  }

  sendTouch(touches: DirectTouch[], essential = false) {
    this.send(encodeTouchInput(touches), essential);
  }

  sendKey(key: string, eventType: 0 | 1 | 2 = 2) {
    if (!directControl.canHumanControl()) return;
    this.send(encodeKeyboardInput(key, eventType), true);
  }

  sendScroll(_x: number, _y: number, _deltaX: number, deltaY: number) {
    this.sendKey(deltaY > 0 ? "ArrowDown" : "ArrowUp", 2);
  }

  rotate() {
    // Native emulator rotation remains an explicit Controller operation; the
    // hardware/scrcpy path implements protocol-native rotation below.
  }

  private send(data: Uint8Array, essential: boolean) {
    const channel = this.inputChannel;
    if (!channel || channel.readyState !== "open") return;
    // Move events may be dropped when a browser is already behind. Down/up
    // and key transitions are never dropped.
    if (!essential && channel.bufferedAmount > 32 * 1024) return;
    const payload = new Uint8Array(data);
    channel.send(payload.buffer as ArrayBuffer);
  }

  private openSocket() {
    if (this.stopped) return;
    const socket = new WebSocket(this.signalingUrl);
    this.socket = socket;
    socket.onmessage = (event) => {
      this.signalChain = this.signalChain
        .then(() => this.handleSignal(JSON.parse(event.data)))
        .catch((error) => this.callbacks.onError(error instanceof Error ? error : new Error(String(error))));
    };
    socket.onerror = () => this.callbacks.onError(new Error(
      "Native WebRTC signaling is unavailable or already in use by another browser tab. Close the other live view or choose scrcpy + WebCodecs.",
    ));
    socket.onclose = () => {
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer || this.stopped) return;
    const delay = Math.min(10_000, 500 * (2 ** this.reconnectAttempt));
    this.reconnectAttempt += 1;
    // Install the timer before closing the peer: RTCPeerConnection.close()
    // may emit another state-change event and must not schedule twice.
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.openSocket();
    }, delay);
    this.cleanupConnection();
    performanceStore.update({
      connectionState: "reconnecting",
      reconnectCount: performanceStore.getSnapshot().reconnectCount + 1,
    });
    this.callbacks.onState("reconnecting");
  }

  private async handleSignal(signal: Signal) {
    if (signal.start) await this.startPeer(signal.start);
    if (signal.type === "offer" || signal.type === "answer") {
      await this.setRemote(signal as RTCSessionDescriptionInit);
    } else if (signal.sdp && typeof signal.sdp === "object") {
      await this.setRemote(signal.sdp);
    }
    if (signal.candidate) await this.addCandidate(signal.candidate);
    if (signal.bye) this.scheduleReconnect();
  }

  private async startPeer(configuration: RTCConfiguration) {
    // The emulator sends both an initial gateway `start` message and its own
    // `start` message. Treat those as idempotent: recreating the peer here
    // abandons the data-channel negotiation from the first connection.
    if (this.peerConnection) return;
    const peer = new RTCPeerConnection({ ...configuration, iceServers: [], bundlePolicy: "max-bundle" });
    this.peerConnection = peer;
    this.pendingCandidates = [];
    this.remoteDescriptionSet = false;
    performanceStore.update({ connectionState: "connecting" });

    peer.ontrack = (event) => this.callbacks.onTrack(event.track);
    peer.onicecandidate = (event) => {
      if (event.candidate && this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ candidate: event.candidate.toJSON() }));
      }
    };
    peer.onconnectionstatechange = () => {
      performanceStore.update({ connectionState: peer.connectionState });
      if (peer.connectionState === "connected") {
        this.reconnectAttempt = 0;
        this.callbacks.onState("streaming");
      } else if (["failed", "closed"].includes(peer.connectionState)) {
        this.scheduleReconnect();
      }
    };

    // The Android emulator is the offerer. Creating the input channel before
    // accepting its offer ensures the offered application m-line is accepted
    // without causing offer glare in the browser.
    const channel = peer.createDataChannel("input");
    this.setInputChannel(channel);
    peer.ondatachannel = (event) => {
      if (event.channel.label === "input") this.setInputChannel(event.channel);
    };
    this.stats = new WebRtcStatsCollector(peer, () => this.inputChannel);
    this.stats.start();

  }

  private applyCodecPreference(transceiver: RTCRtpTransceiver) {
    if (!("setCodecPreferences" in transceiver)) return;
    const capabilities = RTCRtpReceiver.getCapabilities("video")?.codecs || [];
    const preferred = this.profile === "quality" ? "video/VP8" : "video/H264";
    transceiver.setCodecPreferences([
      ...capabilities.filter((codec) => codec.mimeType === preferred),
      ...capabilities.filter((codec) => codec.mimeType !== preferred),
    ]);
  }

  private setInputChannel(channel: RTCDataChannel) {
    this.inputChannel = channel;
    channel.bufferedAmountLowThreshold = 8 * 1024;
    channel.onopen = () => performanceStore.update({ dataChannelState: "open" });
    channel.onclose = () => performanceStore.update({ dataChannelState: "closed" });
  }

  private async setRemote(description: RTCSessionDescriptionInit) {
    if (!this.peerConnection) await this.startPeer({});
    const peer = this.peerConnection;
    if (!peer) return;
    await peer.setRemoteDescription(description);
    this.remoteDescriptionSet = true;
    for (const candidate of this.pendingCandidates.splice(0)) await this.addCandidate(candidate);
    if (description.type === "offer") {
      for (const transceiver of peer.getTransceivers()) {
        const kind = transceiver.receiver.track.kind;
        if (kind === "video") {
          transceiver.direction = "recvonly";
          this.applyCodecPreference(transceiver);
        } else if (kind === "audio") {
          transceiver.direction = this.profile === "quality" ? "recvonly" : "inactive";
        }
      }
      const answer = await peer.createAnswer();
      await peer.setLocalDescription(answer);
      this.socket?.send(JSON.stringify({ sdp: answer }));
    }
  }

  private async addCandidate(candidate: RTCIceCandidateInit | string) {
    if (!this.peerConnection) return;
    if (!this.remoteDescriptionSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    const normalized = typeof candidate === "string"
      ? { candidate, sdpMid: "0", sdpMLineIndex: 0 }
      : candidate;
    await this.peerConnection.addIceCandidate(normalized);
  }

  private cleanupConnection() {
    this.stats?.stop();
    this.stats = undefined;
    if (this.socket) {
      // The gateway forwards this to Android before the WebSocket close frame,
      // allowing the native RTC peer to be destroyed deterministically.
      if (this.socket.readyState === WebSocket.OPEN) {
        try { this.socket.send(JSON.stringify({ bye: true })); } catch { /* best effort */ }
      }
      this.socket.onclose = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = undefined;
    }
    this.inputChannel?.close();
    this.inputChannel = undefined;
    this.peerConnection?.close();
    this.peerConnection = undefined;
    this.pendingCandidates = [];
    this.remoteDescriptionSet = false;
  }
}
